import {
  BrowserCap,
  EstimatedCpuLoad,
  NetworkSpeed,
} from 'src/WebApiController';

export const coalesceUndefinedOnly = <T>(
  x: T | null | undefined,
  y: T | null | undefined
) => {
  return x === undefined ? y : x;
};

export const removeDuplicates = <T = unknown>(arr: T[]): T[] => {
  return [...new Set(arr)];
};

export const compareNumber = (
  numA: number | undefined | null,
  numB: number | undefined | null
) => {
  if (numA == null && numB != null) return 1;
  if (numA != null && numB == null) return -1;

  if (numA != null && numB != null) {
    if (numA !== numB) {
      return numA < numB ? -1 : 1;
    }
  }
  return 0;
};

export const getMemoryUsage = (): {
  jsHeapSizeLimit: number | null;
  totalJSHeapSize: number | null;
  usedJSHeapSize: number | null;
} => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const memory = (window.performance as any)?.memory;
    return {
      jsHeapSizeLimit: memory?.jsHeapSizeLimit,
      totalJSHeapSize: memory?.totalJSHeapSize,
      usedJSHeapSize: memory?.usedJSHeapSize,
    };
  } catch (error) {
    console.warn(
      'Memory profiling is not supported in the current browser',
      error
    );
    return {
      jsHeapSizeLimit: null,
      totalJSHeapSize: null,
      usedJSHeapSize: null,
    };
  }
};

export const getSystemPerformanceIndicators = (
  memoryUsage: ReturnType<typeof getMemoryUsage>
): BrowserCap => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const navigator = window.navigator as any;

    const deviceMemory = navigator.deviceMemory || null;
    const hardwareConcurrency = navigator.hardwareConcurrency || null;

    // Estimate CPU load based on available indicators
    const estimatedCpuLoad = estimateCpuLoad(
      memoryUsage,
      deviceMemory,
      hardwareConcurrency
    );
    const networkSpeed = categorizeNetworkSpeed(getNetworkSpeed());
    const navigation = window.performance?.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming;

    return {
      domContentLoadedMs:
        navigation?.domContentLoadedEventEnd -
          navigation?.domContentLoadedEventStart || null,
      loadCompleteMs:
        navigation?.loadEventEnd - navigation?.loadEventStart || null,
      renderTimeMs:
        navigation?.domComplete - navigation?.domInteractive || null,

      estimatedNetworkSpeed: networkSpeed,
      hardwareConcurrency,
      estimatedCpuLoad,
      browserAgent: navigator.userAgent || null,
    };
  } catch (error) {
    console.warn('System performance indicators not available', error);
    return {
      domContentLoadedMs: null,
      loadCompleteMs: null,
      renderTimeMs: null,

      estimatedNetworkSpeed: null,
      hardwareConcurrency: null,
      estimatedCpuLoad: null,
      browserAgent: null,
    };
  }
};

const estimateCpuLoad = (
  memory: ReturnType<typeof getMemoryUsage>,
  deviceMemory: number | null,
  cores: number | null
): EstimatedCpuLoad => {
  if (!memory.usedJSHeapSize || !memory.jsHeapSizeLimit)
    return EstimatedCpuLoad.Unknown;

  const memoryUsageRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
  const isLowEndDevice = deviceMemory && deviceMemory <= 4;
  const hasLimitedCores = cores && cores <= 2;

  if (memoryUsageRatio > 0.8 || isLowEndDevice || hasLimitedCores) {
    return EstimatedCpuLoad.High;
  } else if (memoryUsageRatio > 0.5) {
    return EstimatedCpuLoad.Medium;
  } else {
    return EstimatedCpuLoad.Low;
  }
};

export const getNetworkSpeed = (): {
  downlink: number | null; // Effective bandwidth in Mbps
  effectiveType: string | null; // '2g', '3g', '4g', 'slow-2g'
  rtt: number | null; // Round trip time in milliseconds
  saveData: boolean | null; // Data saver mode enabled
} => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const navigator = window.navigator as any;
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!connection) {
      return {
        downlink: null,
        effectiveType: null,
        rtt: null,
        saveData: null,
      };
    }

    return {
      downlink: connection.downlink || null, // Mbps
      effectiveType: connection.effectiveType || null,
      rtt: connection.rtt || null, // milliseconds
      saveData: connection.saveData || null,
    };
  } catch (error) {
    console.warn('Network information not available', error);
    return {
      downlink: null,
      effectiveType: null,
      rtt: null,
      saveData: null,
    };
  }
};

export const categorizeNetworkSpeed = (
  networkSpeed: ReturnType<typeof getNetworkSpeed>
): NetworkSpeed => {
  if (!networkSpeed.downlink && !networkSpeed.effectiveType) {
    return NetworkSpeed.Unknown;
  }

  // Check by effective type first
  if (networkSpeed.effectiveType) {
    switch (networkSpeed.effectiveType) {
      case 'slow-2g':
      case '2g':
        return NetworkSpeed.Slow;
      case '3g':
        return NetworkSpeed.Moderate;
      case '4g':
        return NetworkSpeed.Fast;
      default:
        break;
    }
  }

  // Check by downlink speed (Mbps)
  if (networkSpeed.downlink) {
    if (networkSpeed.downlink < 1.5) {
      return NetworkSpeed.Slow;
    } else if (networkSpeed.downlink < 10) {
      return NetworkSpeed.Moderate;
    } else {
      return NetworkSpeed.Fast;
    }
  }

  return NetworkSpeed.Unknown;
};
